// src/services/communalAdmin.service.ts

import api from './api';
import type { ApiResponse } from '../types/api.types';

export interface CommunalAdminCreateRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  department?: string;
  position?: string;
  salary?: number;
  workingHours?: {
    monday?: { start: string; end: string; isWorkingDay: boolean };
    tuesday?: { start: string; end: string; isWorkingDay: boolean };
    wednesday?: { start: string; end: string; isWorkingDay: boolean };
    thursday?: { start: string; end: string; isWorkingDay: boolean };
    friday?: { start: string; end: string; isWorkingDay: boolean };
    saturday?: { start: string; end: string; isWorkingDay: boolean };
    sunday?: { start: string; end: string; isWorkingDay: boolean };
  };
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface CommunalAdminResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  userType: string;
  status: string;
  mustChangePassword: boolean;
  Staff: {
    id: string;
    staffNumber: string;
    role: string;
    assigned_commune_id: string;
    scope_level: string;
    permissions: Record<string, any>;
    isActive: boolean;
    department?: string;
    position?: string;
    salary?: number;
    hireDate: string;
    workingHours?: Record<string, any>;
    emergencyContact?: Record<string, any>;
  };
}

export interface Commune {
  id: string;
  communeName: string;
  communeCode: string;
  region?: string;
  country?: string;
  population?: number;
  totalGroups?: number;
  isActive: boolean;
  communalAdminId?: string;
  CommunalAdmin?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

class CommunalAdminService {
  private BASE_URL = '/auth/register/staff';
  private COMMUNES_URL = '/communes';

  /**
   * Create a new communal admin
   */
  async createCommunalAdmin(data: CommunalAdminCreateRequest): Promise<ApiResponse<CommunalAdminResponse>> {
    const payload = {
      ...data,
      role: 'communal_admin',
    };

    const response = await api.post(this.BASE_URL, payload);
    return response.data;
  }

  /**
   * Get all communes for assignment selection
   */
  async getCommunes(): Promise<ApiResponse<{ communes: Commune[]; pagination: any }>> {
    const response = await api.get(`${this.COMMUNES_URL}?limit=1000&isActive=true`);
    return response.data;
  }

  /**
   * Get communes without assigned communal admin
   */
  async getAvailableCommunes(): Promise<ApiResponse<{ communes: Commune[]; pagination: any }>> {
    const response = await api.get(`${this.COMMUNES_URL}?limit=1000&isActive=true&hasAdmin=false`);
    return response.data;
  }

  /**
   * Check if email is available
   */
  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    try {
      const response = await api.get(`/auth/check-email?email=${encodeURIComponent(email)}`);
      return { available: response.data.available || false };
    } catch (error: any) {
      // If endpoint doesn't exist, assume email checking will be done on submission
      return { available: true };
    }
  }

  /**
   * Generate strong password
   */
  generatePassword(length: number = 12): string {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    const allChars = uppercase + lowercase + numbers + symbols;
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Le mot de passe doit contenir au moins 8 caractères');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une lettre majuscule');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins une lettre minuscule');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un chiffre');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Le mot de passe doit contenir au moins un caractère spécial');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate phone number (international format)
   */
  validatePhone(phone: string): boolean {
    const phonePattern = /^[\+]?[1-9]\d{7,14}$/;
    return phonePattern.test(phone);
  }

  /**
   * Default working hours template
   */
  getDefaultWorkingHours() {
    return {
      monday: { start: '08:00', end: '17:00', isWorkingDay: true },
      tuesday: { start: '08:00', end: '17:00', isWorkingDay: true },
      wednesday: { start: '08:00', end: '17:00', isWorkingDay: true },
      thursday: { start: '08:00', end: '17:00', isWorkingDay: true },
      friday: { start: '08:00', end: '17:00', isWorkingDay: true },
      saturday: { start: '08:00', end: '12:00', isWorkingDay: false },
      sunday: { start: '08:00', end: '12:00', isWorkingDay: false },
    };
  }
}

const communalAdminService = new CommunalAdminService();
export default communalAdminService;